import { computed } from 'vue';
import type { ToolbarMenuItem } from '@/modules/viewer/components/layouts/shared/ToolbarMenuItem';
import { useArtifactsStore } from '../store/useArtifactsStore';
import { TrackEvents, useViewerTrack } from './useViewerTrack';

export const OCR_UPSELL_HREF = '/plans?utm_campaign=upgrade&utm_medium=tooltip&utm_source=viewer';

interface MobileMenuOptions {
  showProcessing?: boolean;
  showUpsell?: boolean;
}

/**
 * Shared composable for OCR text selection and copy actions.
 * Used by both ContentOwnerTopBar (owner) and GuestTopBar (guest).
 */
export const useOcrActions = () => {
  const artifactsStore = useArtifactsStore();
  const { trackItemOwner } = useViewerTrack();

  const isOcrAvailable = computed(() => artifactsStore.isOcrDone && artifactsStore.hasOcrData);
  const isOcrProcessing = computed(() => artifactsStore.isOcrProcessing);
  const isOcrDone = computed(() => artifactsStore.isOcrDone);
  const isSelectionMode = computed(() => artifactsStore.selectionMode);
  const selectedCount = computed(() => artifactsStore.selectedCount);
  const hasSelection = computed(() => artifactsStore.hasSelection);

  const enterSelectionMode = () => {
    artifactsStore.enterSelectionMode();
  };

  const exitSelectionMode = () => {
    artifactsStore.exitSelectionMode();
  };

  const copyAllText = () => {
    artifactsStore.copyAllText();
    trackItemOwner(TrackEvents.VIEWER_OCR_TEXT_COPIED, {
      copy_type: 'all',
      line_count: artifactsStore.ocrLines.length,
    });
  };

  const copySelectedText = () => {
    const lineCount = artifactsStore.selectedCount;
    artifactsStore.copySelectedText();
    trackItemOwner(TrackEvents.VIEWER_OCR_TEXT_COPIED, {
      copy_type: 'selected',
      line_count: lineCount,
    });
  };

  const mobileMenuItems = ({ showProcessing = false, showUpsell = false }: MobileMenuOptions = {}): ToolbarMenuItem[] => {
    if (showUpsell) {
      return [
        {
          name: 'ocr-upsell',
          title: 'Copy text',
          icon: 'general.copyFileFolder',
          type: 'secondary',
          href: OCR_UPSELL_HREF,
        },
      ];
    }

    if (showProcessing && isOcrProcessing.value) {
      return [
        {
          name: 'ocr-processing',
          title: 'Detecting text...',
          icon: 'general.copyFileFolder',
          type: 'secondary',
          disabled: true,
          loading: true,
        },
      ];
    }

    if (isOcrDone.value && !isOcrAvailable.value) {
      return [
        {
          name: 'ocr-no-text',
          title: 'No text detected',
          icon: 'general.copyFileFolder',
          type: 'secondary',
          disabled: true,
        },
      ];
    }

    if (!isOcrAvailable.value) {
      return [];
    }

    if (isSelectionMode.value) {
      return [
        {
          name: 'copy-selected-text',
          title: 'Copy selected text',
          icon: 'general.copyFileFolder',
          type: 'secondary',
          disabled: !hasSelection.value,
          action: copySelectedText,
        },
        {
          name: 'cancel-text-selection',
          title: 'Cancel text selection',
          icon: 'general.close',
          type: 'secondary',
          action: exitSelectionMode,
        },
      ];
    }

    return [
      {
        name: 'select-text',
        title: 'Select text',
        icon: 'general.textFields',
        type: 'secondary',
        action: enterSelectionMode,
      },
      {
        name: 'copy-all-text',
        title: 'Copy all text',
        icon: 'general.copyFileFolder',
        type: 'secondary',
        action: copyAllText,
      },
    ];
  };

  return {
    isOcrAvailable,
    isOcrProcessing,
    isOcrDone,
    isSelectionMode,
    selectedCount,
    hasSelection,
    enterSelectionMode,
    exitSelectionMode,
    copyAllText,
    copySelectedText,
    mobileMenuItems,
  };
};
